import { BaseWebComponentController } from '../../../framework/BaseWebComponentController';
import { LicensePrePaidSubFormWebComponent } from '../component/LicensePrePaidSubFormWebComponent';
import { LicensePrePaidSubFormView } from '../view/LicensePrePaidSubFormView';
import { templateLicensePrePaidSubForm } from '../view/template';
import { LicensePrePaidSubFormRepository } from '../repository/LicensePrePaidSubFormRepository';
import { PrePaidLicenseInformation } from '../../types/PrePaidLicenseInformation';
import moment from 'moment';
import { LicenseExceptions } from '../../types/LicenseExceptions';
import { LicenseStatus } from '../../types/LicenseStatus';
import { CompanyActionContext } from '../../../company/common/types/CompanyActionContext';
import { License } from '../../../company/common/licensingGrid/types/licensing-grid-license';
import { checkExistingIncompatibleProducts, checkLicenseMeetsDependencies } from '../../common/licenseHelper';
import { toggleVisibility } from '../../../utils/htmlElement.utils';
import { LicenseProductIdEnum, PRODUCTS_WITH_MDR } from '@nirvana/shared/license-helper';
import { LicenseDependencies } from '../../types/LicenseDependencies';
import { BaseWebComponentTranslator } from '../../../framework/BaseWebComponentTranslator';
import { translateFn } from '../../../utils/translator.helper';
import { CompanyType } from '../../../company/types/company-type';
import { MdrContactDetailsFormEvents } from '../../../company/common/mdrContactDetails/mdrContactDetailsForm/types';

export class LicensePrePaidSubFormController extends BaseWebComponentController {
  public component: LicensePrePaidSubFormWebComponent;
  public view: LicensePrePaidSubFormView;
  public viewConstructor: typeof LicensePrePaidSubFormView;
  public dataRepository: LicensePrePaidSubFormRepository;
  public dataRepositoryConstructor: typeof LicensePrePaidSubFormRepository;
  private compatibilityIssues: boolean;
  private validLicenseWithIssues: boolean;

  private companyType: CompanyType;

  constructor(component: LicensePrePaidSubFormWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: LicensePrePaidSubFormView,
      viewTemplate: templateLicensePrePaidSubForm,
      dataRepositoryConstructor: LicensePrePaidSubFormRepository,
    });
  }

  computeEventListeners() {
    this.view.setStackedFormFields(this.component.hasStackedFormFields);

    return [
      {
        element: this.view.dom.$checkLicenseValidityButton,
        event: 'click',
        callback: this.checkLicenseButtonHandler,
      },
      {
        element: this.view.dom.$mdrContactDetailsSection,
        event: MdrContactDetailsFormEvents.FORM_GROUP_CREATED,
        callback: this.onMdrContactDetailsFormGroupCreated,
      },
    ];
  }

  private async checkLicenseButtonHandler() {
    this.hasCompatibilityIssues = false;
    // show spinner
    this.view.dom.$checkLicenseValidityButton.showSpinner = true;
    this.view.dom.$checkLicenseValidityButton.disabled = true;

    // set field readonly while checking
    this.view.dom.$licensePrePaidFormField.Input.readonly = true;
    this.view.dom.$licensePrePaidFormField.resetField();

    const companyId = this.view.viewData?.companyId ?? null;

    try {
      const response = await this.dataRepository.getLicenseKeyInfo(
        this.view.dom.$licensePrePaidFormField.value,
        companyId,
        this.view.returnIncompatibleProducts
      );

      if (this.checkLicenseKeyValidity(response) && (await this.checkLicenseMdrRestrictions(response))) {
        // on success
        await this.displayLicenseInformation(response);
        // display MDR Contact Details if needed
        await this.displayMdrContactDetails(response);
        // set product id in form
        this.view.productIdFormField.value = response.data.productId;
        // set isAddon property
        this.view.isAddonProductFormField.value = response.data.isAddon;
      }
    } catch (exception) {
      this.view.dom.$licensePrePaidFormField.Input.readonly = false;

      switch (exception.exceptionType) {
        case 'Exceptions\\NoInternetConnection':
          this.view.dom.$licensePrePaidFormField.setError(
            this.view.licenseExceptionMessages[LicenseExceptions.NO_CONNECTION]
          );
          break;
        case 'Exceptions\\LicenseTemplateDisabledException':
          this.view.dom.$licensePrePaidFormField.setError(
            this.view.licenseExceptionMessages[LicenseExceptions.TEMPLATE_DISABLED]
          );
          break;
        default:
          this.view.dom.$licensePrePaidFormField.setError(
            this.view.licenseExceptionMessages[LicenseExceptions.NO_INFO]
          );
          break;
      }
    } finally {
      // remove the spinner
      this.view.dom.$checkLicenseValidityButton.showSpinner = false;
      this.view.dom.$checkLicenseValidityButton.disabled = false;
    }
  }

  private checkLicenseKeyValidity(response: PrePaidLicenseInformation): boolean {
    const translator = BaseWebComponentTranslator.getInstance();

    // not an active license
    if (this.view.licenseStatusMessages[response.data.status] && response.data.status !== LicenseStatus.ACTIVE) {
      this.view.dom.$licensePrePaidFormField.setError(this.view.licenseStatusMessages[response.data.status]);
      this.view.dom.$licensePrePaidFormField.Input.readonly = false;
      return false;
    }

    // add-on license
    if (response.data.isAddon && !this.checkIsAddonValidity()) {
      this.view.dom.$licensePrePaidFormField.setError(this.view.licenseExceptionMessages[LicenseExceptions.IS_ADDON]);
      this.view.dom.$licensePrePaidFormField.Input.readonly = false;
      return false;
    }

    if (!checkLicenseMeetsDependencies(response.data.dependentProductIds, this.view.viewData?.licenseUsageDetails)) {
      let message = this.view.licenseExceptionMessages[LicenseExceptions.DOES_NOT_HAVE_BASE_LICENSE];

      const isDependentToRiskManagement = LicenseDependencies.dependentToRiskManagementProductId(
        response.data.productId
      );
      const dependentProductId = LicenseDependencies.getDependenciesForProductId(response.data.productId);

      if (isDependentToRiskManagement) {
        message = this.view.licenseExceptionMessages[LicenseExceptions.DOES_NOT_HAVE_RM_BASE_LICENSE];
      }

      if (dependentProductId) {
        message = translateFn('You need to add {base_license_name} first.', {
          base_license_name: translator.translateProductId(dependentProductId),
        });
      }

      this.view.dom.$licensePrePaidFormField.setError(message);
      this.view.dom.$licensePrePaidFormField.Input.readonly = false;
      return false;
    }

    // already in use
    const inUse = response.data.inUse ?? false;
    if (inUse) {
      this.view.dom.$licensePrePaidFormField.setError(
        this.view.licenseExceptionMessages[LicenseExceptions.ALREADY_IN_USE]
      );
      this.view.dom.$licensePrePaidFormField.Input.readonly = false;
      return false;
    }

    if (PRODUCTS_WITH_MDR.includes(response.data.productId) && this.companyType === CompanyType.Partner) {
      const message = translateFn('Partner companies are not eligible for the {LICENSE_NAME} product.', {
        LICENSE_NAME: translator.translateProductId(response.data.productId),
      });

      this.view.dom.$licensePrePaidFormField.setError(message);
      this.view.dom.$licensePrePaidFormField.Input.readonly = false;
      return false;
    }

    return true;
  }

  private async checkLicenseMdrRestrictions(response: PrePaidLicenseInformation): Promise<boolean> {
    if (PRODUCTS_WITH_MDR.includes(response.data.productId)) {
      if (
        this.view.viewData?.canBeManagedByAbove === false ||
        (this.view.viewData?.companyVisibilityTree &&
          this.view.viewData.companyVisibilityTree === this.view.viewData.companyId)
      ) {
        const message = translateFn(
          'Unable to add product. All companies in the management chain, from the target to the Distributor, must allow their direct Partner to assist with security management.'
        );
        this.view.dom.$licensePrePaidFormField.setError(message);
        this.view.dom.$licensePrePaidFormField.Input.readonly = false;
        return false;
      }

      if (!this.view.viewData?.companyVisibilityTree) {
        // When creating a new company that is managed by above
        return true;
      }

      const rootVisibilityTree = await this.dataRepository.getRootVisibilityTree();
      if (rootVisibilityTree !== this.view.viewData?.companyVisibilityTree) {
        const message = translateFn(
          'Unable to add product. All companies in the management chain, from your company to the Distributor, must allow their direct Partner to assist with security management. Please contact your direct partner for assistance.'
        );
        this.view.dom.$licensePrePaidFormField.setError(message);
        this.view.dom.$licensePrePaidFormField.Input.readonly = false;
        return false;
      }
    }

    return true;
  }

  private async displayLicenseInformation(response: PrePaidLicenseInformation) {
    // set checkmark to input
    this.view.dom.$licensePrePaidFormField.Input.showValidIcon = true;

    // set details
    this.view.dom.licenseDetails.$productName.value = this.view.getTranslatedLicenseProductId(response.data.productId);
    this.view.dom.licenseDetails.$validity.value = moment(response.data.expiryDate)
      .tz(this.dependencies.getTimezone())
      .format('DD MMMM YYYY');
    this.view.dom.licenseDetails.$availableSeats.value = response.data.usage.total.toString();

    const availableForServers: number = response.data.availableForServers ?? 0;
    if (availableForServers > 0) {
      this.view.dom.licenseDetails.$availableSeatsForServers.value = availableForServers.toString();
      this.view.dom.licenseDetails.$availableSeatsForServers.classList.remove('hidden');
    }

    const availableForExchangeMailboxes: number = response.data.mailboxes?.total ?? 0;
    if (response.data.productId !== LicenseProductIdEnum.ProductIdMsp && availableForExchangeMailboxes > 0) {
      this.view.dom.licenseDetails.$availableSeatsForExchangeMailboxes.value = availableForExchangeMailboxes.toString();
      this.view.dom.licenseDetails.$availableSeatsForExchangeMailboxes.classList.remove('hidden');
    }

    const availableForMobileDevices: number = response.data.mobiles?.total ?? 0;
    if (availableForMobileDevices > 0) {
      this.view.dom.licenseDetails.$availableSeatsForMobileDevices.value = availableForMobileDevices.toString();
      toggleVisibility(this.view.dom.licenseDetails.$availableSeatsForMobileDevices, true);
    }

    // ensure that the hasIssues flag is reset if we don't have any possible issues to assure that the button is enabled
    const { canBeActivated, activationDenyReason, activationDenyCode } = response.data;
    this.hasIssues = !canBeActivated;

    if (this.hasIssues) {
      // Show the info box with details if there are any issues
      this.view.buildValidLicenseWithIssuesWarningBox(activationDenyReason, activationDenyCode);
    }

    toggleVisibility(this.view.dom.$validLicenseWithIssuesWarning, this.hasIssues);

    if (
      response.data.isAddon &&
      this.hasValidBaseProducts() &&
      checkExistingIncompatibleProducts(
        response.data.incompatibleProductIds,
        this.view.viewData?.licenseUsageDetails,
        true
      )
    ) {
      toggleVisibility(this.view.dom.$incompatibleAddonWithProductError, true);
      this.hasCompatibilityIssues = true;
    } else if (
      this.view.returnIncompatibleProducts &&
      checkExistingIncompatibleProducts(
        response.data.incompatibleProductIds,
        this.view.viewData?.licenseUsageDetails,
        false
      )
    ) {
      toggleVisibility(this.view.dom.$incompatibleProductNotice, true);
    }
    // show details
    this.view.dom.licenseDetails.$licenseDetailsContainer.classList.remove('hidden');

    this.view.dom.$licensePrePaidFormField.resetField();
    this.view.dom.$licensePrePaidFormField.validate();
  }

  private async displayMdrContactDetails(response: PrePaidLicenseInformation): Promise<void> {
    if (!PRODUCTS_WITH_MDR.includes(response.data.productId)) {
      if (!this.view.dom.$mdrContactDetailsSection.started) {
      }
      return;
    }

    if (!this.view.dom.$mdrContactDetailsSection.started) {
      await this.view.startMdrContactDetailsSection(response.data.mdrContactInformation);
    } else {
      this.view.updateMdrContactDetailsSection(response.data.mdrContactInformation);
    }
  }

  private onMdrContactDetailsFormGroupCreated(): void {
    this.view.addFormChildMdrContactInformation();
  }

  private checkIsAddonValidity(): boolean {
    switch (this.view.viewData?.context) {
      case CompanyActionContext.Create:
        return false;

      case CompanyActionContext.MyCompany:
      case CompanyActionContext.Edit:
        return this.hasValidBaseProducts();

      default:
        return true;
    }
  }

  private hasValidBaseProducts() {
    if (!this.view.viewData?.licenseUsageDetails?.data) {
      return false;
    }

    for (const licenseUsageData of this.view.viewData.licenseUsageDetails.data) {
      if (!licenseUsageData.isAddon && License.isValid(licenseUsageData)) {
        return true;
      }
    }

    return false;
  }

  public setCompanyType(companyType: CompanyType) {
    this.companyType = companyType;
    this.view.setCompanyType(companyType);
  }

  public set hasCompatibilityIssues(hasCompatibilityIssues: boolean) {
    this.compatibilityIssues = hasCompatibilityIssues;
  }

  public get hasCompatibilityIssues() {
    return this.compatibilityIssues;
  }

  public set hasIssues(hasIssues: boolean) {
    this.validLicenseWithIssues = hasIssues;
  }

  public get hasIssues() {
    return this.validLicenseWithIssues;
  }
}
